import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import { useNavigate, useParams } from "react-router-dom";
import DeliveryPincodesDialog from "./DeliveryPincodesDialog";
import { EmptyAddCard } from "../../components/settings/EmptyAddCard";
import { getFloorsByBuildingId } from "../../services/WarehouseSettings";
import AddEditFloor from "../../components/settings/AddEditFloor";

const FloorsPage = () => {
  const navigate = useNavigate();
  const { buildingId } = useParams();
  const [floors, setFloors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [pincodesDialogOpen, setPincodesDialogOpen] = useState(false);

  const loadFloors = useCallback(async () => {
    try {
      if (!buildingId) return;
      setLoading(true);
      const data = await getFloorsByBuildingId(Number(buildingId));
      setFloors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load failed:", err);
      setFloors([]);
      setSnackbar({ open: true, message: "Failed to load floors", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [buildingId]);

  useEffect(() => {
    if (buildingId) {
      loadFloors();
    }
  }, [buildingId, loadFloors]);

  const openAddFloor = () => {
    setEditData(null);
    setDialogOpen(true);
  };

  const openEditFloor = (floor: any) => {
    setEditData(floor);
    setDialogOpen(true);
  };

  const handleSuccess = (message: string) => {
    setSnackbar({ open: true, message, severity: "success" });
    loadFloors();
  };

  return (
    <Box sx={{ p: 2, height: "calc(100vh - 3.5rem)", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Button
          onClick={() => navigate("/settings/warehouse")}
          sx={{ border: "none", textTransform: "none", color: "text.primary" }}
          startIcon={<ArrowBackIosNewOutlinedIcon fontSize="small" />}
        >
          Back
        </Button>
        <Button
          variant="outlined"
          onClick={() => setPincodesDialogOpen(true)}
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            color: "primary.main",
            textTransform: "none",
            padding: "6px 16px",
            "&:hover": {
              backgroundColor: "transparent",
              borderColor: "primary.main",
            },
            "&:active": {
              backgroundColor: "transparent",
            },
          }}
          endIcon={<SettingsOutlinedIcon sx={{ color: "primary.main" }} />}
        >
          Delivery Pin codes
        </Button>
      </Box>

      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexShrink: 0 }}>
          <Typography variant="h6">All Floors - {floors.length}</Typography>
          {floors.length > 0 && (
            <Button
              variant="contained"
              endIcon={<AddIcon />}
              onClick={openAddFloor}
              sx={{ borderRadius: "6px", textTransform: "none" }}
            >
              Add Floor
            </Button>
          )}
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", "&::-webkit-scrollbar": { display: "none" } }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
              <CircularProgress />
            </Box>
          ) : floors.length === 0 ? (
            <EmptyAddCard
              onAdd={openAddFloor}
              title="Add Floor"
              description="Click here to add your first floor"
            />
          ) : (
            <Stack spacing={2}>
              {floors.map((floor) => (
              <Box key={floor.id} sx={{ display: "flex", alignItems: "center", gap: 5 }}>
                <Box
                  sx={{
                    flex: 1,
                    position: "relative",
                    height: "75px",
                    borderRadius: 2,
                    backgroundColor: "primary.light",
                    overflow: "hidden",
                    border: "2px dashed",
                    borderColor: "grey.100",
                  }}
                >
                  <Box
                    sx={{
                      width: `${ floor.availablePercentage === 0.0 && floor.usedPercentage >= 100 ? floor.usedPercentage : floor.availablePercentage}%`,
                      height: "100%",
                      backgroundColor: floor.availablePercentage === 0.0 && floor.usedPercentage >= 100 ? "error.main" : floor.availablePercentage <= 30 ? "warning.main" : "success.main",
                      display: "flex",
                      alignItems: "center",
                      pl: 2,
                    }}
                  >
                    <Typography sx={{ color: "background.paper", fontWeight: 600 }}>
                      { floor.availablePercentage === 0.0 && floor.usedPercentage >= 100 ? floor.usedPercentage : floor.availablePercentage}%
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    {floor.floorName}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    startIcon={<Box component="img" src="/edit.svg" alt="Edit" sx={{ width: "1.2rem", height: "1.2rem" }} />}
                    sx={{
                      textTransform: "none",
                      height: "70px",
                      color: "#005EFF",
                      boxShadow: "inset 0 0 8px rgba(0, 94, 255, 0.35)",
                    }}
                    onClick={() => openEditFloor(floor)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityOutlinedIcon />}
                    sx={{
                      textTransform: "none",
                      height: "70px",
                      color: "#005EFF",
                      boxShadow: "inset 0 0 8px rgba(0, 94, 255, 0.35)",
                    }}
                    onClick={() => navigate(`/settings/warehouse/buildings/${buildingId}/floors/${floor.id}/rooms`)}
                  >
                    View
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
          )}
        </Box>
      </Paper>

      <AddEditFloor
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editData={editData}
        buildingId={buildingId!}
        onSuccess={handleSuccess}
      />

      <DeliveryPincodesDialog
        open={pincodesDialogOpen}
        onClose={() => setPincodesDialogOpen(false)}
        onSave={(pincodes) => {
          console.log("Saved pincodes:", pincodes);
          setSnackbar({ open: true, message: "Delivery pincodes saved successfully", severity: "success" });
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: "100%",
            backgroundColor: `${snackbar.severity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
export default FloorsPage;